import { Article, Delete, Visibility } from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Autocomplete,
  TextField,
  Tooltip
} from "@mui/material";
import React, { useState } from "react";
import { map } from "lodash/fp";
import { useQuery, useMutation } from "@apollo/client/react";
import DocumentsViewerModal from "./DocumentsViewer";
import { AttachedFileFragment, GetCompanyTagsDocument, TagAttachmentDocument } from "@/graphql/generated/graphql";

interface UploadPreviewProps {
  file: AttachedFileFragment;
  onRemove?: () => void;
  isUploading?: boolean;
  readOnly?: boolean;
}

const mapId = map("id");

const UploadPreviewItem: React.FC<UploadPreviewProps> = ({
  file,
  onRemove = () => { },
  isUploading = false,
  readOnly = false,
}) => {
  const { data } = useQuery(GetCompanyTagsDocument);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [tagAttachment] = useMutation(TagAttachmentDocument);

  return (
    <Stack direction="row" p={1} alignItems="center" width="100%">
      <Article />
      <Tooltip title={`${file.filename} (click to preview)`}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              ml: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "300px",
              cursor: 'pointer',
              fontSize: 14
            }}
            onClick={() => setViewerUrl(file.previewUrl)}
          >
            {file.filename}
          </Typography>
        </Box>
      </Tooltip>

      <Autocomplete
        multiple
        size="small"
        readOnly={readOnly}
        sx={{ mr: 1, width: "350px" }}
        disableCloseOnSelect
        disabled={isUploading}
        options={data?.getCompanyTags ?? []}
        getOptionLabel={(option) => option.title}
        defaultValue={file.tags ?? []}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="Tags" size="small" sx={{ minWidth: "100px" }} fullWidth />
        )}
        onChange={(_, values) => tagAttachment({ variables: { companyTagIds: mapId(values), id: file.id } })}
      />

      <Tooltip title="View">
        <IconButton
          size="small"
          onClick={() => setViewerUrl(file.previewUrl)}
          aria-label="View file"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>

      {!readOnly &&
        <IconButton size="small" onClick={onRemove} disabled={isUploading}>
          <Delete fontSize="small" color={isUploading ? "disabled" : "error"} />
        </IconButton>}

      {viewerUrl && (
        <DocumentsViewerModal
          urls={[viewerUrl]}
          handleClose={() => setViewerUrl(null)}
        />
      )}
    </Stack>
  );
};

export default UploadPreviewItem;
